/**
 * Default fallback test point catalog (Fluke 175/177/179).
 * Used when the BFF /api/equipment-catalogs endpoint returns no rows
 * (post-seed this should never happen, but the picker degrades gracefully).
 *
 * Based on manufacturer specs per ISO 17025.
 * Test points at ~10%, ~50%, ~100% of full-scale for each range.
 */

export type TestPoint = {
  id: string;
  nominal: number;
  label: string;
};

export type RangeSpec = {
  id: string;
  fullScale: number;
  resolution: number;
  accuracyPct: number;
  accuracyCounts: number;
  testPoints: TestPoint[];
};

export type FunctionSpec = {
  id: string;
  name: string;
  unit: string;
  mode: string;
  defaultFrequency?: number;
  ranges: RangeSpec[];
};

export type TestPointCatalog = {
  model: string;
  standard: string;
  functions: FunctionSpec[];
};

export const DEFAULT_CATALOG: TestPointCatalog = {
  model: 'Fluke 177',
  standard: 'ISO 17025',
  functions: [
    {
      id: 'dcv',
      name: 'DC Voltage',
      unit: 'V',
      mode: 'DC',
      ranges: [
        {
          id: '600mV',
          fullScale: 0.6,
          resolution: 0.0001,
          accuracyPct: 0.09,
          accuracyCounts: 2,
          testPoints: [
            { id: 'dcv-600mv-10', nominal: 0.06, label: '60 mV' },
            { id: 'dcv-600mv-50', nominal: 0.3, label: '300 mV' },
            { id: 'dcv-600mv-100', nominal: 0.6, label: '600 mV' }
          ]
        },
        {
          id: '6V',
          fullScale: 6,
          resolution: 0.001,
          accuracyPct: 0.09,
          accuracyCounts: 2,
          testPoints: [
            { id: 'dcv-6v-10', nominal: 0.6, label: '0.6 V' },
            { id: 'dcv-6v-50', nominal: 3.0, label: '3 V' },
            { id: 'dcv-6v-100', nominal: 6.0, label: '6 V' }
          ]
        },
        {
          id: '60V',
          fullScale: 60,
          resolution: 0.01,
          accuracyPct: 0.09,
          accuracyCounts: 2,
          testPoints: [
            { id: 'dcv-60v-10', nominal: 6.0, label: '6 V' },
            { id: 'dcv-60v-50', nominal: 30.0, label: '30 V' },
            { id: 'dcv-60v-100', nominal: 60.0, label: '60 V' }
          ]
        },
        {
          id: '600V',
          fullScale: 600,
          resolution: 0.1,
          accuracyPct: 0.09,
          accuracyCounts: 2,
          testPoints: [
            { id: 'dcv-600v-10', nominal: 60.0, label: '60 V' },
            { id: 'dcv-600v-50', nominal: 300.0, label: '300 V' },
            { id: 'dcv-600v-100', nominal: 600.0, label: '600 V' }
          ]
        },
        {
          id: '1000V',
          fullScale: 1000,
          resolution: 1,
          accuracyPct: 0.09,
          accuracyCounts: 2,
          testPoints: [
            { id: 'dcv-1000v-10', nominal: 100.0, label: '100 V' },
            { id: 'dcv-1000v-50', nominal: 500.0, label: '500 V' },
            { id: 'dcv-1000v-100', nominal: 1000.0, label: '1000 V' }
          ]
        }
      ]
    },
    {
      id: 'acv',
      name: 'AC Voltage',
      unit: 'V',
      mode: 'AC',
      defaultFrequency: 60,
      ranges: [
        {
          id: '600mV',
          fullScale: 0.6,
          resolution: 0.0001,
          accuracyPct: 1.0,
          accuracyCounts: 3,
          testPoints: [
            { id: 'acv-600mv-10', nominal: 0.06, label: '60 mV' },
            { id: 'acv-600mv-50', nominal: 0.3, label: '300 mV' },
            { id: 'acv-600mv-100', nominal: 0.6, label: '600 mV' }
          ]
        },
        {
          id: '6V',
          fullScale: 6,
          resolution: 0.001,
          accuracyPct: 1.0,
          accuracyCounts: 3,
          testPoints: [
            { id: 'acv-6v-10', nominal: 0.6, label: '0.6 V' },
            { id: 'acv-6v-50', nominal: 3.0, label: '3 V' },
            { id: 'acv-6v-100', nominal: 6.0, label: '6 V' }
          ]
        },
        {
          id: '60V',
          fullScale: 60,
          resolution: 0.01,
          accuracyPct: 1.0,
          accuracyCounts: 3,
          testPoints: [
            { id: 'acv-60v-10', nominal: 6.0, label: '6 V' },
            { id: 'acv-60v-50', nominal: 30.0, label: '30 V' },
            { id: 'acv-60v-100', nominal: 60.0, label: '60 V' }
          ]
        },
        {
          id: '600V',
          fullScale: 600,
          resolution: 0.1,
          accuracyPct: 1.0,
          accuracyCounts: 3,
          testPoints: [
            { id: 'acv-600v-10', nominal: 60.0, label: '60 V' },
            { id: 'acv-600v-50', nominal: 300.0, label: '300 V' },
            { id: 'acv-600v-100', nominal: 600.0, label: '600 V' }
          ]
        },
        {
          id: '1000V',
          fullScale: 1000,
          resolution: 1,
          accuracyPct: 1.0,
          accuracyCounts: 3,
          testPoints: [
            { id: 'acv-1000v-10', nominal: 100.0, label: '100 V' },
            { id: 'acv-1000v-50', nominal: 500.0, label: '500 V' },
            { id: 'acv-1000v-100', nominal: 1000.0, label: '1000 V' }
          ]
        }
      ]
    },
    {
      id: 'ohm',
      name: 'Resistance',
      unit: '\u03A9',
      mode: 'OHM',
      ranges: [
        {
          id: '600',
          fullScale: 600,
          resolution: 0.1,
          accuracyPct: 0.9,
          accuracyCounts: 2,
          testPoints: [
            { id: 'ohm-600-50', nominal: 300, label: '300 \u03A9' },
            { id: 'ohm-600-100', nominal: 600, label: '600 \u03A9' }
          ]
        },
        {
          id: '6k',
          fullScale: 6000,
          resolution: 1,
          accuracyPct: 0.9,
          accuracyCounts: 2,
          testPoints: [
            { id: 'ohm-6k-50', nominal: 3000, label: '3 k\u03A9' },
            { id: 'ohm-6k-100', nominal: 6000, label: '6 k\u03A9' }
          ]
        },
        {
          id: '60k',
          fullScale: 60000,
          resolution: 10,
          accuracyPct: 0.9,
          accuracyCounts: 2,
          testPoints: [
            { id: 'ohm-60k-50', nominal: 30000, label: '30 k\u03A9' },
            { id: 'ohm-60k-100', nominal: 60000, label: '60 k\u03A9' }
          ]
        },
        {
          id: '600k',
          fullScale: 600000,
          resolution: 100,
          accuracyPct: 0.9,
          accuracyCounts: 2,
          testPoints: [
            { id: 'ohm-600k-50', nominal: 300000, label: '300 k\u03A9' },
            { id: 'ohm-600k-100', nominal: 600000, label: '600 k\u03A9' }
          ]
        },
        {
          id: '6M',
          fullScale: 6000000,
          resolution: 1000,
          accuracyPct: 0.9,
          accuracyCounts: 2,
          testPoints: [
            { id: 'ohm-6m-50', nominal: 3000000, label: '3 M\u03A9' },
            { id: 'ohm-6m-100', nominal: 6000000, label: '6 M\u03A9' }
          ]
        },
        {
          id: '40M',
          fullScale: 40000000,
          resolution: 10000,
          accuracyPct: 0.9,
          accuracyCounts: 2,
          testPoints: [
            { id: 'ohm-40m-50', nominal: 20000000, label: '20 M\u03A9' },
            { id: 'ohm-40m-100', nominal: 40000000, label: '40 M\u03A9' }
          ]
        }
      ]
    },
    {
      id: 'cap',
      name: 'Capacitance',
      unit: 'F',
      mode: 'CAP',
      ranges: [
        {
          id: '10nF',
          fullScale: 1e-8,
          resolution: 1e-12,
          accuracyPct: 1.2,
          accuracyCounts: 2,
          testPoints: [{ id: 'cap-10n', nominal: 1e-8, label: '10 nF' }]
        },
        {
          id: '100nF',
          fullScale: 1e-7,
          resolution: 1e-11,
          accuracyPct: 1.2,
          accuracyCounts: 2,
          testPoints: [{ id: 'cap-100n', nominal: 1e-7, label: '100 nF' }]
        },
        {
          id: '1uF',
          fullScale: 1e-6,
          resolution: 1e-10,
          accuracyPct: 1.2,
          accuracyCounts: 2,
          testPoints: [{ id: 'cap-1u', nominal: 1e-6, label: '1 \u00B5F' }]
        },
        {
          id: '10uF',
          fullScale: 1e-5,
          resolution: 1e-9,
          accuracyPct: 1.2,
          accuracyCounts: 2,
          testPoints: [{ id: 'cap-10u', nominal: 1e-5, label: '10 \u00B5F' }]
        },
        {
          id: '100uF',
          fullScale: 1e-4,
          resolution: 1e-8,
          accuracyPct: 1.2,
          accuracyCounts: 2,
          testPoints: [{ id: 'cap-100u', nominal: 1e-4, label: '100 \u00B5F' }]
        },
        {
          id: '10000uF',
          fullScale: 1e-2,
          resolution: 1e-6,
          accuracyPct: 1.2,
          accuracyCounts: 2,
          testPoints: [
            { id: 'cap-10000u', nominal: 1e-2, label: '10000 \u00B5F' }
          ]
        }
      ]
    },
    {
      id: 'temp',
      name: 'Temperature',
      unit: '\u00B0C',
      mode: 'TEMP',
      ranges: [
        {
          id: 'temp',
          fullScale: 400,
          resolution: 0.1,
          accuracyPct: 1.0,
          accuracyCounts: 10,
          testPoints: [
            { id: 'temp-low', nominal: 0, label: '0 \u00B0C' },
            { id: 'temp-mid', nominal: 100, label: '100 \u00B0C' },
            { id: 'temp-high', nominal: 400, label: '400 \u00B0C' }
          ]
        }
      ]
    }
  ]
};
